import type { LocalDatabase } from '../db/types';
import type { ChangeOperation } from '@couplespace/sync';

import type { BlockRecord } from './types';
import { enqueueOutbox } from '../db/outbox';
import { softDeleteBlockLocal, upsertBlockLocal } from '../db/queries';
import { getSyncEngine } from '../sync/engine';

async function getDb(): Promise<LocalDatabase> {
  const engine = getSyncEngine();
  const db = engine.getDatabase();
  if (!db) {
    await engine.start();
    const ready = engine.getDatabase();
    if (!ready) {
      throw new Error('Local database not ready');
    }
    return ready;
  }
  return db;
}

function scheduleSync(): void {
  getSyncEngine().schedulePush();
}

async function writeBlock(
  operation: ChangeOperation,
  block: BlockRecord,
  baseSyncVersion: number | null,
): Promise<BlockRecord> {
  const database = await getDb();
  if (operation === 'delete') {
    await softDeleteBlockLocal(database, block.id);
  } else {
    await upsertBlockLocal(database, block);
  }
  await enqueueOutbox(database, {
    tableName: 'blocks',
    operation,
    row: block as unknown as Record<string, unknown>,
    baseSyncVersion,
  });
  scheduleSync();
  return block;
}

export async function createBlockLocal(input: {
  id: string;
  parentId: string | null;
  type: string;
  title?: string;
  content?: Record<string, unknown>;
  icon?: string;
  position?: number;
  createdBy: string;
}): Promise<BlockRecord> {
  const now = new Date().toISOString();
  const block: BlockRecord = {
    id: input.id,
    parentId: input.parentId,
    position: input.position ?? 0,
    type: input.type,
    title: input.title ?? null,
    content: input.content ?? {},
    icon: input.icon ?? null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    createdBy: input.createdBy,
    updatedBy: input.createdBy,
    actorKind: 'human',
    vectorClock: { [input.createdBy]: 1 },
    syncVersion: 1,
  };
  const created = await writeBlock('insert', block, null);
  if (input.type === 'database') {
    const { ensureDatabaseMetadataLocal } = await import('../databases/create-metadata-local');
    await ensureDatabaseMetadataLocal(input.id, input.createdBy);
  }
  return created;
}

export async function updateBlockLocal(
  block: BlockRecord,
  patch: {
    title?: string;
    content?: Record<string, unknown>;
    icon?: string | null;
    parentId?: string | null;
    position?: number;
  },
  updatedBy: string,
): Promise<BlockRecord> {
  const updated: BlockRecord = {
    ...block,
    title: patch.title !== undefined ? patch.title : block.title,
    content: patch.content !== undefined ? patch.content : block.content,
    icon: patch.icon !== undefined ? patch.icon : block.icon,
    parentId: patch.parentId !== undefined ? patch.parentId : block.parentId,
    position: patch.position !== undefined ? patch.position : block.position,
    updatedAt: new Date().toISOString(),
    updatedBy,
    syncVersion: block.syncVersion,
  };
  return writeBlock('update', updated, block.syncVersion);
}

export async function deleteBlockLocal(block: BlockRecord): Promise<void> {
  const database = await getDb();
  const { collectSubtreeBlockIdsLocal, queryBlockIncludingDeleted } = await import('../db/queries');
  const descendantIds = await collectSubtreeBlockIdsLocal(database, block.id);
  const ordered = [...descendantIds.reverse(), block.id];

  for (const id of ordered) {
    const target =
      id === block.id ? block : await queryBlockIncludingDeleted(database, id);
    if (!target || target.deletedAt) {
      continue;
    }
    const deleted: BlockRecord = {
      ...target,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await writeBlock('delete', deleted, target.syncVersion);
  }
}

export async function restoreBlockLocal(blockId: string): Promise<BlockRecord> {
  const database = await getDb();
  const {
    collectAllSubtreeBlockIdsLocal,
    queryBlockIncludingDeleted,
  } = await import('../db/queries');
  const root = await queryBlockIncludingDeleted(database, blockId);
  if (!root || !root.deletedAt) {
    throw new Error('Page not found in trash');
  }

  const descendantIds = await collectAllSubtreeBlockIdsLocal(database, blockId);
  const subtreeBlocks = await Promise.all(
    [blockId, ...descendantIds].map((id) => queryBlockIncludingDeleted(database, id)),
  );
  const toRestore = subtreeBlocks.filter(
    (row): row is BlockRecord => row != null && row.deletedAt != null,
  );

  let restoredRoot = root;
  for (const target of toRestore) {
    const restored: BlockRecord = {
      ...target,
      deletedAt: null,
      updatedAt: new Date().toISOString(),
    };
    restoredRoot = await writeBlock('update', restored, target.syncVersion);
  }

  return restoredRoot;
}

export async function fetchBlockTreeLocal(parentId: string | null): Promise<BlockRecord[]> {
  const database = await getDb();
  const { queryBlockTree } = await import('../db/queries');
  return queryBlockTree(database, parentId);
}

export async function fetchPageDocumentLocal(pageId: string): Promise<{
  block: BlockRecord;
  contentBlocks: BlockRecord[];
}> {
  const database = await getDb();
  const { queryBlock, queryPageContentBlocks } = await import('../db/queries');
  const block = await queryBlock(database, pageId);
  if (!block) {
    throw new Error('Page not found');
  }
  const contentBlocks = await queryPageContentBlocks(database, pageId);
  return { block, contentBlocks };
}
